import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Receipt,
  Save,
  MessageSquare,
  ArrowLeft
} from 'lucide-react';
import { useGst } from '../../context/GstContext';
import {
  BillingLineItem,
  InvoiceType,
  InvoiceTemplateId,
  MerchantInvoice
} from '../../types/gst';
import { calculateInvoiceTotals, INVOICE_TEMPLATES, generateInvoiceWhatsAppLink } from '../../services/billingService';
import { CustomerBillingSection } from '../../components/billing/CustomerBillingSection';
import { LineItemEditor } from '../../components/billing/LineItemEditor';
import { PaymentSummaryCard } from '../../components/billing/PaymentSummaryCard';
import { toast } from '../../components/ui';

export const CreateInvoicePage: React.FC = () => {
  const navigate = useNavigate();
  const {
    clientId,
    clientLegalName,
    activeGstin,
    billingSettings,
    saveMerchantInvoice,
    financialYear,
    taxPeriod
  } = useGst();

  // Seller Details
  const sellerGstin = activeGstin || billingSettings?.gstin || '27AABCU9603R1ZM';
  const sellerStateCode = sellerGstin.substring(0, 2) || '27';

  // Invoice Form State
  const [invoiceType] = useState<InvoiceType>('B2B');
  const [invoiceNumber, setInvoiceNumber] = useState<string>(
    `${billingSettings?.invoicePrefix || 'INV/25-26/'}${billingSettings?.nextInvoiceSequence || 101}`
  );
  const [invoiceDate, setInvoiceDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState<string>(
    new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  const [templateId, setTemplateId] = useState<InvoiceTemplateId>(
    billingSettings?.defaultTemplateId || 'CLASSIC_GST'
  );

  // Buyer Details
  const [customerName, setCustomerName] = useState<string>('');
  const [customerGstin, setCustomerGstin] = useState<string>('');
  const [customerPhone, setCustomerPhone] = useState<string>('');
  const [customerEmail, setCustomerEmail] = useState<string>('');
  const [customerBillingAddress, setCustomerBillingAddress] = useState<string>('');
  const [placeOfSupply, setPlaceOfSupply] = useState<string>('27-Maharashtra');

  // Line Items
  const [lineItems, setLineItems] = useState<BillingLineItem[]>([
    {
      id: 'item_1',
      description: 'AI Cloud Platform Implementation & Services',
      hsnSac: '998313',
      quantity: 1,
      unit: 'NOS',
      unitPrice: 50000,
      discountPercent: 0,
      discountAmount: 0,
      taxableAmount: 50000,
      gstRate: 18,
      cgstAmount: 4500,
      sgstAmount: 4500,
      igstAmount: 0,
      cessAmount: 0,
      totalAmount: 59000
    }
  ]);

  const [notes] = useState<string>('Thank you for your business!');
  const [terms] = useState<string>(
    billingSettings?.defaultTerms || 'Payment due within 15 days.\nSubject to local jurisdiction.'
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Recompute deterministic totals on any change
  const totals = calculateInvoiceTotals(lineItems, sellerStateCode, placeOfSupply);

  // Line Item Handlers
  const handleAddItem = () => {
    const newItem: BillingLineItem = {
      id: `item_${Date.now()}`,
      description: '',
      hsnSac: '998313',
      quantity: 1,
      unit: 'NOS',
      unitPrice: 0,
      discountPercent: 0,
      discountAmount: 0,
      taxableAmount: 0,
      gstRate: 18,
      cgstAmount: 0,
      sgstAmount: 0,
      igstAmount: 0,
      cessAmount: 0,
      totalAmount: 0
    };
    setLineItems([...lineItems, newItem]);
  };

  const handleRemoveItem = (index: number) => {
    if (lineItems.length === 1) return;
    setLineItems(lineItems.filter((_, idx) => idx !== index));
  };

  const handleUpdateItem = (index: number, field: keyof BillingLineItem, val: any) => {
    const updated = [...lineItems];
    updated[index] = { ...updated[index], [field]: val };
    setLineItems(updated);
  };

  const handleSave = async (andWhatsApp: boolean = false) => {
    if (!customerName.trim()) {
      toast.error('Customer Name is required.');
      return;
    }
    if (lineItems.length === 0 || !lineItems[0].description) {
      toast.error('At least one item description is required.');
      return;
    }

    setIsSaving(true);
    try {
      const invoiceData: MerchantInvoice = {
        id: `inv_${Date.now()}`,
        clientId: clientId || 'default_client',
        invoiceNumber,
        invoiceDate,
        dueDate,
        invoiceType,
        financialYear,
        taxPeriod,
        sellerGstin,
        sellerLegalName: billingSettings?.companyName || clientLegalName || 'Company Enterprise',
        sellerTradeName: billingSettings?.tradeName || billingSettings?.companyName || clientLegalName || '',
        sellerAddress: billingSettings?.address || 'Mumbai, Maharashtra',
        sellerPhone: billingSettings?.phone || '',
        sellerEmail: billingSettings?.email || '',
        sellerStateCode,
        customerName: customerName.trim(),
        customerGstin: customerGstin.trim(),
        customerPhone: customerPhone.trim(),
        customerEmail: customerEmail.trim(),
        customerBillingAddress: customerBillingAddress.trim(),
        placeOfSupply,
        isInterState: totals.isInterState,
        lineItems: totals.items,
        subtotalTaxable: totals.subtotalTaxable,
        totalCgst: totals.totalCgst,
        totalSgst: totals.totalSgst,
        totalIgst: totals.totalIgst,
        totalCess: totals.totalCess,
        totalTax: totals.totalTax,
        roundOff: totals.roundOff,
        grandTotal: totals.grandTotal,
        status: 'ISSUED',
        paidAmount: 0,
        balanceDue: totals.grandTotal,
        templateId,
        notes: notes.trim(),
        termsAndConditions: terms.trim(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await saveMerchantInvoice(invoiceData);
      toast.success(`Invoice #${invoiceNumber} saved & synced to Sales Ledger!`);

      if (andWhatsApp) {
        const link = generateInvoiceWhatsAppLink(invoiceData, billingSettings || undefined);
        window.open(link, '_blank');
      }

      navigate('/billing');
    } catch (err: any) {
      console.error('Save invoice error:', err);
      toast.error('Failed to save invoice: ' + (err?.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Top Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/billing')}
            className="p-1.5 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-slate-300 transition"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <Receipt className="h-6 w-6 text-cyan-400" />
              <span>Create GST Tax Invoice</span>
            </h1>
            <p className="text-xs text-slate-400 mt-0.5">
              High-speed billing &bull; Auto-detects CGST+SGST vs IGST &bull; Syncs with GSTR-1
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => handleSave(true)}
            disabled={isSaving}
            className="flex items-center gap-1.5 rounded-xl border border-emerald-500/40 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 px-3.5 py-2 text-xs font-semibold transition"
          >
            <MessageSquare className="h-3.5 w-3.5 text-emerald-400" />
            <span>Save & WhatsApp</span>
          </button>

          <button
            type="button"
            onClick={() => handleSave(false)}
            disabled={isSaving}
            className="flex items-center gap-1.5 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-black px-4 py-2 text-xs font-bold transition shadow-sm shadow-cyan-500/25"
          >
            <Save className="h-4 w-4" />
            <span>{isSaving ? 'Saving...' : 'Save Invoice'}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column (2 Cols): Buyer & Items Form */}
        <div className="lg:col-span-2 space-y-6">
          <CustomerBillingSection
            customerName={customerName}
            setCustomerName={setCustomerName}
            customerGstin={customerGstin}
            setCustomerGstin={setCustomerGstin}
            customerPhone={customerPhone}
            setCustomerPhone={setCustomerPhone}
            customerEmail={customerEmail}
            setCustomerEmail={setCustomerEmail}
            customerBillingAddress={customerBillingAddress}
            setCustomerBillingAddress={setCustomerBillingAddress}
            placeOfSupply={placeOfSupply}
            setPlaceOfSupply={setPlaceOfSupply}
            isInterState={totals.isInterState}
          />

          <LineItemEditor
            lineItems={lineItems}
            computedItems={totals.items}
            onAddItem={handleAddItem}
            onRemoveItem={handleRemoveItem}
            onUpdateItem={handleUpdateItem}
          />
        </div>

        {/* Right Column (1 Col): Meta, Layout & Payment Summary */}
        <div className="space-y-6">
          <div className="rounded-2xl border border-white/10 bg-[#131316] p-5 space-y-4 text-xs">
            <div className="text-xs font-bold uppercase text-cyan-400 border-b border-white/5 pb-2">
              Invoice Meta & Layout
            </div>

            <div>
              <label className="text-[11px] font-medium text-slate-300 block mb-1">Invoice Number</label>
              <input
                type="text"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-3 py-1.5 text-white font-mono text-xs focus:outline-none focus:border-cyan-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-medium text-slate-300 block mb-1">Invoice Date</label>
                <input
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-2.5 py-1.5 text-white text-xs focus:outline-none focus:border-cyan-500"
                />
              </div>
              <div>
                <label className="text-[11px] font-medium text-slate-300 block mb-1">Due Date</label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-2.5 py-1.5 text-white text-xs focus:outline-none focus:border-cyan-500"
                />
              </div>
            </div>

            <div>
              <label className="text-[11px] font-medium text-slate-300 block mb-1">
                Print Template Layout
              </label>
              <select
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value as InvoiceTemplateId)}
                className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-3 py-2 text-white text-xs focus:outline-none focus:border-cyan-500"
              >
                {INVOICE_TEMPLATES.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name} — {t.tagline}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <PaymentSummaryCard
            subtotalTaxable={totals.subtotalTaxable}
            totalIgst={totals.totalIgst}
            totalCgst={totals.totalCgst}
            totalSgst={totals.totalSgst}
            roundOff={totals.roundOff}
            grandTotal={totals.grandTotal}
            isInterState={totals.isInterState}
            isSaving={isSaving}
            onSave={() => handleSave(false)}
          />
        </div>
      </div>
    </div>
  );
};
